import React, { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Percent, Store, Tag } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { backendGet } from '../lib/backendClient';
import { useAuth } from '../contexts/AuthContext';
import { DEFAULT_BANNER } from '../lib/storeImages';
import './Promotions.css';

function normalizeList(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.results)) return payload.results;
  return [];
}

function formatMoney(value) {
  const numeric = Number(value);
  const normalized = Number.isFinite(numeric) ? numeric : 0;
  return `R$ ${normalized.toFixed(2).replace('.', ',')}`;
}

function getPrimaryAddress(profileData) {
  const addresses = Array.isArray(profileData?.addresses) ? profileData.addresses : [];
  if (addresses.length > 0) {
    return addresses.find((addr) => addr?.is_primary) || addresses[0];
  }
  if (profileData?.address && typeof profileData.address === 'object') {
    return profileData.address;
  }
  return null;
}

export default function Promotions() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [promotions, setPromotions] = useState([]);
  const [coords, setCoords] = useState(null);

  useEffect(() => {
    let mounted = true;

    async function loadProfileCoords() {
      if (!user) return;
      try {
        const profileData = await backendGet('/api/auth/profile');
        if (!mounted) return;
        const address = getPrimaryAddress(profileData);
        const lat = Number(address?.lat ?? address?.latitude);
        const lng = Number(address?.lng ?? address?.longitude);
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
          setCoords({ lat, lng });
        }
      } catch (_) {
        // sem coordenadas do usuario, segue sem filtro de raio
      }
    }

    loadProfileCoords();
    return () => { mounted = false; };
  }, [user]);

  useEffect(() => {
    let mounted = true;

    async function loadPromotions() {
      setLoading(true);
      setError('');

      try {
        const params = new URLSearchParams();
        if (Number.isFinite(coords?.lat) && Number.isFinite(coords?.lng)) {
          params.set('lat', String(coords.lat));
          params.set('lng', String(coords.lng));
          params.set('radius_km', '7');
        }
        const query = params.toString();
        const payload = await backendGet(`/api/products/promotions${query ? `?${query}` : ''}`, {
          skipAuth: !user,
        });
        if (!mounted) return;
        setPromotions(normalizeList(payload));
      } catch (err) {
        if (!mounted) return;
        setError(err?.message || 'Nao foi possivel carregar as promocoes.');
      } finally {
        if (mounted) setLoading(false);
      }
    }

    loadPromotions();
    return () => { mounted = false; };
  }, [coords?.lat, coords?.lng, user]);

  const groupedByStore = useMemo(() => {
    const map = new Map();
    promotions.forEach((item) => {
      const store = item?.restaurant || {};
      const product = item?.product || {};
      const storeId = String(store.id || 'unknown');
      if (!map.has(storeId)) {
        map.set(storeId, {
          id: storeId,
          name: store.name || 'Loja',
          logo_url: store.logo_url,
          is_open: store.is_open !== false,
          products: [],
        });
      }
      map.get(storeId).products.push({
        id: product.id,
        name: product.name || product.nome || 'Produto',
        image_url: product.image_url || product.imagem,
        promotion: product.promotion || {},
        base_price: Number(product.preco || 0),
        final_price: Number(product?.promotion?.final_price ?? product.price_promotional ?? 0),
      });
    });
    return Array.from(map.values());
  }, [promotions]);

  const pageMetrics = useMemo(() => {
    const totalStores = groupedByStore.length;
    const totalOffers = groupedByStore.reduce((acc, store) => acc + store.products.length, 0);
    const maxDiscount = groupedByStore.reduce((acc, store) => {
      const storeMax = store.products.reduce((storeAcc, product) => {
        const discount = Number(product?.promotion?.total_discount_percent || product?.promotion?.discount_percent || 0);
        return Math.max(storeAcc, discount);
      }, 0);
      return Math.max(acc, storeMax);
    }, 0);
    return {
      totalStores,
      totalOffers,
      maxDiscount,
    };
  }, [groupedByStore]);

  return (
    <div className="promotions-page">
      <div className="promotions-container">
        <button className="promotions-back" onClick={() => navigate('/')}>
          <ArrowLeft size={16} />
          Voltar
        </button>

        <div className="promotions-header">
          <span className="promotions-kicker">Descontos em destaque</span>
          <h1>Promocoes</h1>
          <p>Produtos com desconto ativo das lojas perto de voce.</p>
          <div className="promotions-metrics">
            <div className="promotions-metric-card">
              <strong>{pageMetrics.totalStores}</strong>
              <span>Lojas com oferta</span>
            </div>
            <div className="promotions-metric-card">
              <strong>{pageMetrics.totalOffers}</strong>
              <span>Itens em promocao</span>
            </div>
            <div className="promotions-metric-card">
              <strong>{Math.round(pageMetrics.maxDiscount || 0)}%</strong>
              <span>Maior desconto</span>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="promotions-state">Carregando promocoes...</div>
        ) : error ? (
          <div className="promotions-state error">{error}</div>
        ) : groupedByStore.length === 0 ? (
          <div className="promotions-state">Nenhum produto promocional encontrado no momento.</div>
        ) : (
          <div className="promotions-stores">
            {groupedByStore.map((store) => (
              <section key={store.id} className="promotions-store-card">
                <div className="promotions-store-header">
                  <div className="promotions-store-title">
                    <img
                      src={store.logo_url || DEFAULT_BANNER}
                      alt={store.name}
                      onError={(event) => {
                        event.currentTarget.onerror = null;
                        event.currentTarget.src = DEFAULT_BANNER;
                      }}
                    />
                    <div>
                      <h2>{store.name}</h2>
                      <p>{store.products.length} {store.products.length === 1 ? 'oferta ativa' : 'ofertas ativas'}</p>
                    </div>
                  </div>
                  <span className={`promotions-store-status ${store.is_open ? 'open' : 'closed'}`}>
                    {store.is_open ? 'Aberta' : 'Fechada'}
                  </span>
                  <button
                    className="promotions-store-btn"
                    onClick={() => navigate(`/store/${store.id}`)}
                  >
                    <Store size={16} />
                    Ver loja
                  </button>
                </div>

                <div className="promotions-products-grid">
                  {store.products.map((product) => {
                    const discount = Number(product?.promotion?.total_discount_percent || product?.promotion?.discount_percent || 0);
                    const savings = Math.max(0, Number(product.base_price || 0) - Number(product.final_price || 0));
                    return (
                      <article key={product.id} className="promotions-product-card">
                        <div className="promotions-product-image-wrap">
                          <img
                            src={product.image_url || DEFAULT_BANNER}
                            alt={product.name}
                            onError={(event) => {
                              event.currentTarget.onerror = null;
                              event.currentTarget.src = DEFAULT_BANNER;
                            }}
                          />
                          <span className="promotions-product-badge">
                            <Percent size={14} />
                            {discount.toFixed(0)}% OFF
                          </span>
                        </div>

                        <div className="promotions-product-content">
                          <h3>{product.name}</h3>
                          <div className="promotions-price-row">
                            <span className="old-price">{formatMoney(product.base_price)}</span>
                            <span className="new-price">{formatMoney(product.final_price)}</span>
                          </div>
                          <p className="promotions-savings">Voce economiza {formatMoney(savings)}</p>
                          <div className="promotions-tags">
                            {product?.promotion?.admin_coupon_applied ? (
                              <span className="tag-coupon">
                                <Tag size={14} />
                                Cupom {product?.promotion?.admin_coupon_code || 'ADMIN'}
                              </span>
                            ) : null}
                          </div>
                        </div>
                      </article>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
